import {memo} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Button, NumberedList, ResponsiveSheet} from '@chekinapp/ui';
import {EXPLAINER_LIST} from './styles';
import {formatMoney} from '../money';
import type {HoldSectionVariant} from '../holdSectionCopy';

type Props = {
  isOpen: boolean;
  close: () => void;
  variant?: HoldSectionVariant;
  holdTotal?: number;
  currencySymbol?: string;
};

const STEP_KEYS_BY_VARIANT: Record<HoldSectionVariant, [string, string, string]> = {
  hold: [
    'hold_step_reserved',
    'hold_step_nothing_happens',
    'hold_step_released_after_checkout',
  ],
  authorisation: [
    'authorisation_step_registered',
    'authorisation_step_nothing_blocked',
    'authorisation_step_expires_after_checkout',
  ],
  both: ['both_step_when_you_pay', 'both_step_difference', 'both_step_how_they_end'],
};

const FIRST_STEP_WITH_AMOUNT: Partial<Record<HoldSectionVariant, string>> = {
  hold: 'hold_step_reserved_with_amount',
  authorisation: 'authorisation_step_registered_with_amount',
};

const COPY_BY_VARIANT: Record<HoldSectionVariant, {title: string; description: string}> =
  {
    hold: {title: 'holds_explained', description: 'holds_explained_description'},
    authorisation: {
      title: 'authorisations_explained',
      description: 'authorisations_explained_description',
    },
    both: {
      title: 'holds_and_authorisations_explained',
      description: 'holds_and_authorisations_explained_description',
    },
  };

const HoldInfoModal = memo(
  ({isOpen, close, variant = 'hold', holdTotal, currencySymbol = ''}: Props) => {
    const {t} = useTranslation();
    const hasAmount = Boolean(holdTotal && currencySymbol);
    const amount = formatMoney(holdTotal ?? 0, currencySymbol);

    const [firstStep, ...restSteps] = STEP_KEYS_BY_VARIANT[variant];
    const stepKeys = [
      (hasAmount && FIRST_STEP_WITH_AMOUNT[variant]) || firstStep,
      ...restSteps,
    ];
    const copy = COPY_BY_VARIANT[variant];

    return (
      <ResponsiveSheet
        open={isOpen}
        onClose={close}
        title={t(copy.title)}
        description={t(copy.description)}
        className="hold-info-modal"
        dialogClassName="max-w-[400px] sm:max-w-[400px]"
        closeOnOverlayClick
        contentClassName="px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-8"
      >
        <div className="mt-4 flex flex-col gap-5">
          <NumberedList size="sm" className={EXPLAINER_LIST}>
            {stepKeys.map(stepKey => (
              <Trans key={stepKey} i18nKey={stepKey} values={{amount}} />
            ))}
          </NumberedList>
          <Button onClick={close}>{t('got_it')}</Button>
        </div>
      </ResponsiveSheet>
    );
  },
);

export {HoldInfoModal};
